import { useEffect, useMemo, useState } from "react";
import Carregando from "./Carregando";
import { CAMPO } from "./ui";
import {
  REGIMES,
  SETORES,
  colunasDaRegra,
  criarObrigacao,
  descricaoParecRepetirARegra,
  excluirObrigacao,
  excluirExcecao,
  excluirFeriado,
  lerCatalogo,
  rotuloDoSetor,
  salvarExcecao,
  salvarFeriado,
  salvarObrigacao,
  type Catalogo,
  type Obrigacao,
  type Regime,
  type Setor,
} from "../lib/calendario";
import {
  NOMES_DOS_MESES,
  dataBr,
  explicarRegra,
  indexarExcecoes,
  montarCalendario,
  vencimentoDaObrigacao,
  type Ajuste,
  type Periodicidade,
  type Regra,
  type TipoDeDiaUtil,
} from "../lib/calendarioFiscal";
import { Etiqueta } from "./CalendarioUi";
import { slugDeTitulo } from "../lib/slug";

/**
 * Admin › Calendário: o catálogo de obrigações, os feriados de exceção e os
 * decretos (000106, 000109).
 *
 * ## Por que "confirmar a regra" é um botão, e não um detalhe
 *
 * O catálogo foi carregado do app da Lovable, onde cada obrigação era uma linha
 * com DATA. O ETL inferiu a regra a partir das datas digitadas e acertou 19 das
 * 20 — mas inferência é hipótese, e uma delas é a lei. Enquanto ninguém abrir a
 * norma, a obrigação fica com `regra_confirmada = false` e o aluno vê "regra não
 * conferida" na linha dela.
 *
 * O ETL NUNCA marca nada como confirmado, e rodar a carga de novo não desfaz o
 * que foi confirmado aqui — é o princípio 6 (decisão de administração não é
 * desfeita por esteira) aplicado ao catálogo. Quem confirma é gente, com a
 * norma aberta.
 *
 * ## A prévia existe para a regra não ser abstrata
 *
 * Ao lado da regra, os próximos seis vencimentos que ela produz. Sem isso,
 * escolher entre "dia 20 antecipado" e "dia 20 prorrogado" é escolher entre duas
 * frases; com isso, é escolher entre 19/06 e 22/06. Foi a diferença que a origem
 * errou sete vezes num mês só.
 *
 * ## Aqui não há RPC
 *
 * Escrita direta na tabela, com a policy de `pode_editar('calendario')` (000297). RPC existe para
 * esconder decisão — a posse do aluno, que nunca pode vir de um parâmetro. No
 * catálogo não há posse: há permissão, e ela é da policy.
 */
export default function AdminCalendario() {
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aba, setAba] = useState<"obrigacoes" | "feriados">("obrigacoes");
  const [criando, setCriando] = useState(false);

  async function recarregar() {
    const c = await lerCatalogo();
    if (!c) {
      setErro("Não conseguimos ler o catálogo. Confira se o seu papel é de admin.");
      return;
    }
    setCatalogo(c);
  }

  useEffect(() => {
    void recarregar();
  }, []);

  if (erro && !catalogo) {
    return <p className="rounded-xl bg-erro-fundo p-4 text-sm text-erro-tinta">{erro}</p>;
  }
  if (!catalogo) return <Carregando />;

  const pendentes = catalogo.obrigacoes.filter((o) => !o.regraConfirmada).length;

  return (
    <div>
      <header className="pb-5">
        <h1 className="text-xl font-bold text-titulo">Calendário Tributário</h1>
        <p className="max-w-2xl pt-2 text-sm leading-relaxed text-cinza">
          Cada obrigação guarda a <strong className="text-tinta">regra</strong>, não a data:
          é isso que faz a ferramenta valer para qualquer ano. As {catalogo.obrigacoes.length}{" "}
          obrigações vieram da carga do app antigo, com a regra{" "}
          <strong className="text-tinta">deduzida</strong> das datas que ele mostrava.
        </p>
        {pendentes > 0 && (
          <p className="mt-3 rounded-xl bg-alerta-fundo p-4 text-sm leading-relaxed text-alerta-tinta">
            <strong>
              {pendentes} {pendentes === 1 ? "regra ainda não foi conferida" : "regras ainda não foram conferidas"}{" "}
              na norma.
            </strong>{" "}
            Até serem, o aluno vê "regra não conferida" na linha. Confirmar é dizer que
            alguém abriu a legislação — a carga nunca confirma nada, e rodá-la de novo não
            desfaz o que você confirmar aqui.
          </p>
        )}
      </header>

      <nav className="flex gap-1 border-b border-borda pb-px" aria-label="Seções do calendário">
        {(
          [
            ["obrigacoes", `Obrigações (${catalogo.obrigacoes.length})`],
            [
              "feriados",
              `Feriados de exceção (${catalogo.feriadosExtras.length + catalogo.feriadosRemovidos.length})`,
            ],
          ] as const
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            onClick={() => setAba(valor)}
            aria-current={aba === valor ? "page" : undefined}
            className={`-mb-px rounded-t-xl border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              aba === valor
                ? "border-laranja text-titulo"
                : "border-transparent text-cinza hover:text-tinta"
            }`}
          >
            {rotulo}
          </button>
        ))}
      </nav>

      {erro && <p className="mt-4 rounded-xl bg-erro-fundo p-4 text-sm text-erro-tinta">{erro}</p>}

      {aba === "obrigacoes" ? (
        <>
          {/* O caminho de CADASTRO. Ele faltava até 25/08: a tela sabia editar as
              20 que o ETL trouxe e não sabia criar a 21ª, embora a policy
              `calendario_obrigacoes_admin_insert` existisse desde a 000106. Tela
              que não faz o que o banco permite é lacuna, não desenho. */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-5">
            <p className="text-sm text-cinza">
              {catalogo.obrigacoes.length} obrigações no catálogo ·{" "}
              {catalogo.obrigacoes.filter((o) => !o.ativa).length} inativas
            </p>
            <button
              type="button"
              onClick={() => setCriando((v) => !v)}
              className={criando ? "btn-contorno text-sm" : "pill-cta text-sm"}
            >
              {criando ? "Cancelar" : "Nova obrigação"}
            </button>
          </div>

          {criando && (
            <NovaObrigacao
              catalogo={catalogo}
              onPronto={async () => {
                setCriando(false);
                await recarregar();
              }}
              onErro={setErro}
            />
          )}

          <ul className="flex flex-col gap-3 pt-5">
            {catalogo.obrigacoes.map((o) => (
              <LinhaDeObrigacao
                key={o.id}
                obrigacao={o}
                catalogo={catalogo}
                onMudou={recarregar}
                onErro={setErro}
              />
            ))}
          </ul>
        </>
      ) : (
        <Feriados catalogo={catalogo} onMudou={recarregar} onErro={setErro} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A prévia: os próximos seis vencimentos que a regra EM EDIÇÃO produz
// ---------------------------------------------------------------------------
/**
 * Sai do MESMO módulo que a tela do aluno usa — se saísse de outro lugar, a
 * prévia poderia mostrar uma data e o aluno ver outra.
 *
 * E é um hook compartilhado, não uma cópia em cada formulário: quando o cadastro
 * de obrigação nova entrou (25/08), a alternativa era repetir estas vinte linhas —
 * e duas cópias da mesma travessia divergem no primeiro conserto, com a
 * particularidade de que aqui a divergência seria INVISÍVEL (as duas telas
 * mostrariam datas plausíveis).
 *
 * `obrigacaoId` nulo é o caso do cadastro: obrigação que ainda não existe não tem
 * decreto, então a prévia mostra a regra pura.
 */
function usarPrevia(
  regra: Regra,
  periodicidade: Periodicidade,
  catalogo: Catalogo,
  obrigacaoId: string | null,
) {
  return useMemo(() => {
    const hoje = new Date();
    const datas: string[] = [];
    let ano = hoje.getFullYear();
    let mes = hoje.getMonth() + 1;
    const excecoes = indexarExcecoes(
      obrigacaoId ? (catalogo.excecoes.get(obrigacaoId) ?? []) : [],
    );
    const caches = new Map<number, ReturnType<typeof montarCalendario>>();
    for (let i = 0; i < 12 && datas.length < 6; i += 1) {
      if (!caches.has(ano)) {
        caches.set(
          ano,
          montarCalendario(ano, {
            acrescentar: catalogo.feriadosExtras,
            remover: catalogo.feriadosRemovidos,
          }),
        );
      }
      const v = vencimentoDaObrigacao(caches.get(ano)!, regra, periodicidade, ano, mes, excecoes);
      if (v.data) datas.push(v.data);
      mes += 1;
      if (mes > 12) {
        mes = 1;
        ano += 1;
      }
    }
    return datas;
  }, [regra, periodicidade, catalogo, obrigacaoId]);
}

// ---------------------------------------------------------------------------
// Uma obrigação
// ---------------------------------------------------------------------------
function LinhaDeObrigacao({
  obrigacao,
  catalogo,
  onMudou,
  onErro,
}: {
  obrigacao: Obrigacao;
  catalogo: Catalogo;
  onMudou: () => Promise<void>;
  onErro: (m: string | null) => void;
}) {
  const [aberta, setAberta] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [regra, setRegra] = useState<Regra>(obrigacao.regra);
  const [periodicidade, setPeriodicidade] = useState<Periodicidade>(obrigacao.periodicidade);
  const [setor, setSetor] = useState<Setor>(obrigacao.setor);
  const [geral, setGeral] = useState(obrigacao.geral);
  const [regimes, setRegimes] = useState<Set<Regime>>(new Set(obrigacao.regimes));
  const [ativa, setAtiva] = useState(obrigacao.ativa);
  const [titulo, setTitulo] = useState(obrigacao.titulo);
  const [descricao, setDescricao] = useState(obrigacao.descricao);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);

  const previa = usarPrevia(regra, periodicidade, catalogo, obrigacao.id);

  const excecoesDela = catalogo.excecoes.get(obrigacao.id) ?? [];

  async function gravar(confirmar: boolean) {
    setSalvando(true);
    onErro(null);
    const r = await salvarObrigacao(obrigacao.id, {
      ...colunasDaRegra(regra, periodicidade),
      titulo: titulo.trim(),
      descricao: descricao.trim(),
      setor,
      geral,
      regimes: [...regimes],
      ativa,
      regra_confirmada: confirmar,
      ...(confirmar
        ? { regra_origem: "conferida na norma pela equipe de conteúdo" }
        : {}),
    });
    setSalvando(false);
    if (!r.ok) {
      onErro(r.mensagem);
      return;
    }
    await onMudou();
    setAberta(false);
  }

  return (
    <li className="rounded-2xl border-[1.5px] border-borda bg-superficie">
      <div className="flex flex-wrap items-start justify-between gap-3 p-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-titulo">{obrigacao.titulo}</h2>
            <Etiqueta>{rotuloDoSetor(obrigacao.setor)}</Etiqueta>
            {obrigacao.regraConfirmada ? (
              <Etiqueta tom="sucesso">regra conferida</Etiqueta>
            ) : (
              <Etiqueta tom="alerta">não conferida</Etiqueta>
            )}
            {!obrigacao.ativa && <Etiqueta tom="erro">inativa</Etiqueta>}
          </div>
          <p className="pt-1 text-xs text-cinza">
            {explicarRegra(obrigacao.regra, obrigacao.periodicidade)}
          </p>
          {obrigacao.regraOrigem && (
            <p className="pt-1 text-xs leading-relaxed text-cinza/80">
              <span className="font-medium">de onde veio:</span> {obrigacao.regraOrigem}
            </p>
          )}
          {/* A medição vai para onde o conserto acontece. Dez das vinte descrições
              repetem o prazo que a regra já diz, e as quatro maiores do catálogo
              estão nesse grupo — numa linha do PGDAS-D o contador lê a regra e
              depois 392 caracteres que a repetem. Reescrever é da equipe; o código
              só acusa. */}
          {descricaoParecRepetirARegra(obrigacao.descricao) && (
            <p className="pt-1.5 text-xs leading-relaxed text-alerta-tinta">
              <strong>A descrição parece repetir o prazo</strong> ({obrigacao.descricao.length}{" "}
              caracteres) — a tela do aluno já mostra "
              {explicarRegra(obrigacao.regra, obrigacao.periodicidade)}" logo ao lado. Vale
              reescrever dizendo o que a obrigação É, e deixar o prazo para a regra.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setAberta((a) => !a)}
          className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-link transition hover:bg-chip"
        >
          {aberta ? "Fechar" : "Editar regra"}
        </button>
      </div>

      {aberta && (
        <div className="border-t border-borda p-4">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
            <div className="flex flex-col gap-4">
              {/* Título e descrição faltavam aqui, e a falta era pior do que
                  parece: eu havia posto, nesta mesma tela, um aviso dizendo "vale
                  reescrever a descrição" — numa tela onde ela não era editável.
                  Tela que pede uma ação que ela mesma não permite é pior que tela
                  sem o aviso. */}
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="text-xs font-semibold text-cinza">Título</span>
                <input
                  className={CAMPO}
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  maxLength={200}
                />
                <span className="text-xs leading-relaxed text-cinza">
                  identificador:{" "}
                  <span className="font-mono text-[0.6875rem] text-tinta">{obrigacao.slug}</span> —
                  ele NÃO muda quando você reescreve o título, e é assim de propósito: é por
                  ele que a carga reconhece esta obrigação. Mudá-lo faria a próxima carga
                  criar uma segunda linha.
                </span>
              </label>

              <label className="flex flex-col gap-1.5 text-sm">
                <span className="text-xs font-semibold text-cinza">
                  Descrição{" "}
                  <span className="font-normal">
                    ({descricao.length} caracteres, máximo 4.000)
                  </span>
                </span>
                <textarea
                  className={`${CAMPO} h-28 py-2.5`}
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  maxLength={4000}
                />
              </label>

              <EditorDeRegra
                regra={regra}
                periodicidade={periodicidade}
                onRegra={setRegra}
                onPeriodicidade={setPeriodicidade}
              />

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="text-xs font-semibold text-cinza">Setor</span>
                  <select
                    className={CAMPO}
                    value={setor}
                    onChange={(e) => setSetor(e.target.value as Setor)}
                  >
                    {SETORES.map((s) => (
                      <option key={s.valor} value={s.valor}>
                        {s.rotulo}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex flex-col justify-end gap-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={geral}
                      onChange={(e) => setGeral(e.target.checked)}
                      className="size-4 accent-laranja"
                    />
                    <span className="text-tinta">Vale para todos os regimes</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={ativa}
                      onChange={(e) => setAtiva(e.target.checked)}
                      className="size-4 accent-laranja"
                    />
                    <span className="text-tinta">Ativa (aparece para o aluno)</span>
                  </label>
                </div>
              </div>

              {!geral && (
                <section role="group" aria-labelledby={`reg-${obrigacao.id}`}>
                  <h3 id={`reg-${obrigacao.id}`} className="pb-1.5 text-xs font-semibold text-cinza">
                    Regimes
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {REGIMES.map((r) => {
                      const ligado = regimes.has(r.valor);
                      return (
                        <button
                          key={r.valor}
                          type="button"
                          onClick={() =>
                            setRegimes((a) => {
                              const n = new Set(a);
                              if (n.has(r.valor)) n.delete(r.valor);
                              else n.add(r.valor);
                              return n;
                            })
                          }
                          aria-pressed={ligado}
                          className={`rounded-full border-[1.5px] px-3 py-1.5 text-xs font-medium transition ${
                            ligado
                              ? "border-laranja bg-laranja text-azul"
                              : "border-borda bg-superficie text-cinza"
                          }`}
                        >
                          {r.rotulo}
                        </button>
                      );
                    })}
                  </div>
                  {regimes.size === 0 && (
                    <p className="pt-2 text-xs text-erro-tinta">
                      Sem "vale para todos" e sem nenhum regime, a obrigação não apareceria
                      para ninguém — o banco recusa este estado.
                    </p>
                  )}
                </section>
              )}
            </div>

            <aside className="rounded-xl bg-superficie-2 p-4">
              <h3 className="text-xs font-semibold text-cinza">
                O que esta regra produz
              </h3>
              <p className="pt-1.5 text-xs leading-relaxed text-tinta">
                {explicarRegra(regra, periodicidade)}
              </p>
              <ul className="flex flex-col gap-1 pt-3">
                {previa.map((d) => (
                  <li key={d} className="text-sm font-medium text-titulo">
                    {d ? dataBr(d) : "—"}
                  </li>
                ))}
                {previa.length === 0 && (
                  <li className="text-sm text-erro-tinta">
                    Esta regra não gera data nenhuma nos próximos 12 meses.
                  </li>
                )}
              </ul>
              {excecoesDela.length > 0 && (
                <div className="pt-4">
                  <h3 className="text-xs font-semibold text-cinza">
                    Decretos nesta obrigação
                  </h3>
                  <ul className="flex flex-col gap-2 pt-2">
                    {excecoesDela.map((e) => (
                      <li key={`${e.ano}-${e.mes}`} className="text-xs leading-relaxed text-cinza">
                        <strong className="text-tinta">
                          {NOMES_DOS_MESES[e.mes - 1]} de {e.ano}:
                        </strong>{" "}
                        {e.data ? dataBr(e.data) : "não vence"}
                        <br />
                        {e.motivo}
                        <button
                          type="button"
                          onClick={() =>
                            void (async () => {
                              const r = await excluirExcecao(obrigacao.id, e.ano, e.mes);
                              if (!r.ok) onErro(r.mensagem);
                              else await onMudou();
                            })()
                          }
                          className="block pt-1 text-xs font-medium text-link underline"
                        >
                          apagar este decreto
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <NovoDecreto obrigacaoId={obrigacao.id} onMudou={onMudou} onErro={onErro} />
            </aside>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-5">
            <button
              type="button"
              onClick={() => void gravar(true)}
              disabled={salvando || (!geral && regimes.size === 0)}
              className="rounded-full bg-laranja px-5 py-2.5 text-sm font-semibold text-azul transition hover:brightness-105 disabled:opacity-60"
            >
              {salvando ? "Salvando…" : "Salvar e confirmar a regra"}
            </button>
            <button
              type="button"
              onClick={() => void gravar(false)}
              disabled={salvando || (!geral && regimes.size === 0)}
              className="rounded-full border-[1.5px] border-borda px-5 py-2.5 text-sm font-medium text-tinta transition hover:border-laranja disabled:opacity-60"
            >
              Salvar sem confirmar
            </button>
            <p className="text-xs leading-relaxed text-cinza">
              Confirmar diz que alguém abriu a norma. Enquanto não, o aluno vê o aviso.
            </p>
          </div>

          {/* Excluir é o último recurso, e a tela diz por quê. O caminho normal é
              DESATIVAR (a caixa acima), que tira a obrigação da tela do aluno sem
              destruir nada — é a assimetria da casa: errar para mais é reversível,
              errar para menos não. A FK de `calendario_feito` é `on delete
              cascade`, então excluir uma obrigação usada apagaria entregas
              marcadas por contadores que nunca vão saber por quê. Quem recusa de
              verdade é `excluirObrigacao`, que conta as marcas antes. */}
          <div className="mt-4 border-t border-borda pt-3">
            {confirmandoExclusao ? (
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs leading-relaxed text-erro-tinta">
                  Excluir apaga a obrigação para todo mundo. Se ela já tem entrega marcada, a
                  exclusão é recusada — desative em vez disso.
                </p>
                <button
                  type="button"
                  onClick={() =>
                    void (async () => {
                      setSalvando(true);
                      const r = await excluirObrigacao(obrigacao.id);
                      setSalvando(false);
                      setConfirmandoExclusao(false);
                      if (!r.ok) onErro(r.mensagem);
                      else await onMudou();
                    })()
                  }
                  disabled={salvando}
                  className="rounded-full bg-erro-fundo px-4 py-1.5 text-xs font-semibold text-erro-tinta transition disabled:opacity-60"
                >
                  Excluir mesmo
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmandoExclusao(false)}
                  className="rounded-full px-4 py-1.5 text-xs font-medium text-cinza"
                >
                  Não
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmandoExclusao(true)}
                className="text-xs font-medium text-cinza underline transition hover:text-erro-tinta"
              >
                excluir esta obrigação
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Cadastrar uma obrigação NOVA
// ---------------------------------------------------------------------------
/**
 * O formulário de cadastro.
 *
 * Três coisas que ele faz e que a tela de edição não precisava fazer:
 *
 * 1. **mostra o SLUG que o título vai gerar, antes de gravar.** O slug é a
 *    identidade que a carga usa no `on conflict`, e ele nunca muda depois — então
 *    a pessoa tem de ver o que está criando. Sem isso, "DAS" e "D.A.S." pareceriam
 *    títulos diferentes e gerariam slugs diferentes (`das` e `d-a-s`), e a carga
 *    da próxima vez criaria uma terceira linha;
 * 2. **avisa quando o slug já existe**, com o título de quem o tem. A mensagem do
 *    Postgres (`duplicate key value violates unique constraint`) não é acionável;
 *    "já existe uma obrigação com este título" é;
 * 3. **mostra a prévia dos próximos seis vencimentos** enquanto a regra é
 *    escolhida — a mesma prévia da edição, do mesmo módulo. Escolher entre "dia 20
 *    antecipado" e "dia 20 prorrogado" sem ver as datas é escolher entre duas
 *    frases; com as datas, é escolher entre 19/06 e 22/06.
 *
 * E o que ele NÃO faz: não deixa criar confirmada por padrão. Quem cadastra à mão
 * pode ter a norma aberta — e aí marca —, mas o padrão é o mesmo das 20 da carga:
 * não conferida até alguém dizer que conferiu.
 */
function NovaObrigacao({
  catalogo,
  onPronto,
  onErro,
}: {
  catalogo: Catalogo;
  onPronto: () => Promise<void>;
  onErro: (m: string | null) => void;
}) {
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [setor, setSetor] = useState<Setor>("fiscal");
  const [regra, setRegra] = useState<Regra>({ tipo: "dia-fixo", dia: 20, ajuste: "antecipar" });
  const [periodicidade, setPeriodicidade] = useState<Periodicidade>({ tipo: "mensal" });
  const [geral, setGeral] = useState(false);
  const [regimes, setRegimes] = useState<Set<Regime>>(new Set());
  const [salvando, setSalvando] = useState(false);

  const slug = slugDeTitulo(titulo);
  const jaExiste = catalogo.obrigacoes.find((o) => o.slug === slug);
  const previa = usarPrevia(regra, periodicidade, catalogo, null);
  const podeGravar =
    Boolean(slug) && !jaExiste && (geral || regimes.size > 0) && !salvando && previa.length > 0;

  async function gravar(confirmada: boolean) {
    setSalvando(true);
    onErro(null);
    const r = await criarObrigacao({
      titulo,
      descricao,
      setor,
      regra,
      periodicidade,
      geral,
      regimes: [...regimes],
      confirmada,
    });
    setSalvando(false);
    if (!r.ok) {
      onErro(r.mensagem);
      return;
    }
    await onPronto();
  }

  return (
    <section
      role="group"
      aria-labelledby="nova-obrigacao"
      className="mt-4 rounded-cf-lg border-[1.5px] border-laranja/40 bg-superficie p-5"
    >
      <h2 id="nova-obrigacao" className="text-base font-semibold text-titulo">
        Nova obrigação
      </h2>
      <p className="max-w-[70ch] pt-1.5 text-sm leading-relaxed text-cinza">
        Ela vai aparecer no calendário de todos os alunos que têm o card, em todo mês que a
        regra alcançar — inclusive nos anos que ninguém digitou. Confira a prévia antes de
        gravar.
      </p>

      <div className="grid gap-4 pt-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-tinta">Título</span>
            <input
              className={CAMPO}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              maxLength={200}
              placeholder="Dirbi — Declaração de Incentivos e Benefícios"
            />
            {/* O slug à vista, porque ele é identidade e não muda depois. */}
            {titulo.trim() !== "" && (
              <span className="text-xs leading-relaxed text-cinza">
                identificador:{" "}
                <span className="font-mono text-[0.6875rem] text-tinta">{slug || "(vazio)"}</span>
                {!slug && (
                  <strong className="block pt-0.5 text-erro-tinta">
                    O título precisa de ao menos uma letra ou número.
                  </strong>
                )}
                {jaExiste && (
                  <strong className="block pt-0.5 text-erro-tinta">
                    Já existe: “{jaExiste.titulo}”. Diferencie o título, ou edite a que existe.
                  </strong>
                )}
                {slug && !jaExiste && (
                  <span className="block pt-0.5">
                    Ele não muda depois, nem se você reescrever o título — é por ele que a
                    carga reconhece esta obrigação.
                  </span>
                )}
              </span>
            )}
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-tinta">
              Descrição <span className="font-normal text-cinza">(o que a obrigação é)</span>
            </span>
            <textarea
              className={`${CAMPO} h-24 py-2.5`}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              maxLength={4000}
              placeholder="Quem entrega, o que declara, e o que muda por regime."
            />
            <span className="text-xs leading-relaxed text-cinza">
              Não repita o prazo aqui: a tela do aluno já escreve “
              {explicarRegra(regra, periodicidade)}” ao lado do título.
            </span>
          </label>

          <EditorDeRegra
            regra={regra}
            periodicidade={periodicidade}
            onRegra={setRegra}
            onPeriodicidade={setPeriodicidade}
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs font-semibold text-cinza">Setor</span>
              <select
                className={CAMPO}
                value={setor}
                onChange={(e) => setSetor(e.target.value as Setor)}
              >
                {SETORES.map((s) => (
                  <option key={s.valor} value={s.valor}>
                    {s.rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-end gap-2 pb-3 text-sm">
              <input
                type="checkbox"
                checked={geral}
                onChange={(e) => setGeral(e.target.checked)}
                className="size-4 accent-laranja"
              />
              <span className="text-tinta">Vale para todos os regimes</span>
            </label>
          </div>

          {!geral && (
            <section role="group" aria-labelledby="regimes-nova">
              <h3 id="regimes-nova" className="pb-1.5 text-xs font-semibold text-cinza">
                Regimes
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {REGIMES.map((r) => {
                  const ligado = regimes.has(r.valor);
                  return (
                    <button
                      key={r.valor}
                      type="button"
                      onClick={() =>
                        setRegimes((a) => {
                          const n = new Set(a);
                          if (n.has(r.valor)) n.delete(r.valor);
                          else n.add(r.valor);
                          return n;
                        })
                      }
                      aria-pressed={ligado}
                      className={`rounded-full border-[1.5px] px-3 py-1.5 text-xs font-medium transition ${
                        ligado
                          ? "border-laranja bg-laranja font-semibold text-azul"
                          : "border-borda bg-superficie text-cinza"
                      }`}
                    >
                      {r.rotulo}
                    </button>
                  );
                })}
              </div>
              {regimes.size === 0 && (
                <p className="pt-2 text-xs text-erro-tinta">
                  Sem “vale para todos” e sem nenhum regime, a obrigação não apareceria para
                  ninguém — o banco recusa este estado.
                </p>
              )}
            </section>
          )}
        </div>

        <aside className="rounded-xl bg-superficie-2 p-4">
          <h3 className="text-xs font-semibold text-cinza">O que esta regra produz</h3>
          <p className="pt-1.5 text-xs leading-relaxed text-tinta">
            {explicarRegra(regra, periodicidade)}
          </p>
          <ul className="flex flex-col gap-1 pt-3">
            {previa.map((d) => (
              <li key={d} className="text-sm font-medium text-titulo">
                {d ? dataBr(d) : "—"}
              </li>
            ))}
            {previa.length === 0 && (
              <li className="text-sm text-erro-tinta">
                Esta regra não gera data nenhuma nos próximos 12 meses.
              </li>
            )}
          </ul>
        </aside>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-5">
        <button
          type="button"
          onClick={() => void gravar(false)}
          disabled={!podeGravar}
          className="pill-cta text-sm disabled:opacity-60"
        >
          {salvando ? "Cadastrando…" : "Cadastrar"}
        </button>
        <button
          type="button"
          onClick={() => void gravar(true)}
          disabled={!podeGravar}
          className="btn-contorno text-sm disabled:opacity-60"
        >
          Cadastrar já conferida na norma
        </button>
        <p className="text-xs leading-relaxed text-cinza">
          O padrão é entrar <strong>não conferida</strong>, como as 20 da carga — o aluno vê o
          aviso até alguém abrir a legislação.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// O editor da regra — as três formas, e só os campos de cada uma
// ---------------------------------------------------------------------------
function EditorDeRegra({
  regra,
  periodicidade,
  onRegra,
  onPeriodicidade,
}: {
  regra: Regra;
  periodicidade: Periodicidade;
  onRegra: (r: Regra) => void;
  onPeriodicidade: (p: Periodicidade) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-xs font-semibold text-cinza">Forma da regra</span>
          <select
            className={CAMPO}
            value={regra.tipo}
            onChange={(e) => {
              const t = e.target.value;
              // Trocar de forma troca os CAMPOS, e cada forma nasce completa: a
              // constraint do banco recusa dia-fixo sem ajuste, n-dia-util sem
              // tipo de dia útil, e por aí. Deixar um campo vazio aqui só
              // adiaria o erro para o clique em salvar.
              if (t === "ultimo-dia-util") onRegra({ tipo: "ultimo-dia-util" });
              else if (t === "n-dia-util") onRegra({ tipo: "n-dia-util", n: 5, diaUtil: "bancario" });
              else onRegra({ tipo: "dia-fixo", dia: 20, ajuste: "antecipar" });
            }}
          >
            <option value="dia-fixo">Dia fixo do mês</option>
            <option value="n-dia-util">N-ésimo dia útil</option>
            <option value="ultimo-dia-util">Último dia útil</option>
          </select>
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-xs font-semibold text-cinza">Quando acontece</span>
          <select
            className={CAMPO}
            value={periodicidade.tipo}
            onChange={(e) =>
              onPeriodicidade(
                e.target.value === "anual"
                  ? { tipo: "anual", mes: periodicidade.tipo === "anual" ? periodicidade.mes : 5 }
                  : { tipo: "mensal" },
              )
            }
          >
            <option value="mensal">Todo mês</option>
            <option value="anual">Uma vez por ano</option>
          </select>
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {regra.tipo === "dia-fixo" && (
          <>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs font-semibold text-cinza">Dia</span>
              <input
                className={CAMPO}
                type="number"
                min={1}
                max={31}
                value={regra.dia}
                onChange={(e) =>
                  onRegra({ ...regra, dia: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })
                }
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs font-semibold text-cinza">
                Se cair em dia não útil
              </span>
              <select
                className={CAMPO}
                value={regra.ajuste}
                onChange={(e) => onRegra({ ...regra, ajuste: e.target.value as Ajuste })}
              >
                <option value="antecipar">Antecipa para o dia útil anterior</option>
                <option value="prorrogar">Prorroga para o dia útil seguinte</option>
                <option value="nenhum">Não mexe — vence no dia não útil</option>
              </select>
            </label>
          </>
        )}

        {regra.tipo === "n-dia-util" && (
          <>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs font-semibold text-cinza">Qual dia útil</span>
              <input
                className={CAMPO}
                type="number"
                min={1}
                max={25}
                value={regra.n}
                onChange={(e) =>
                  onRegra({ ...regra, n: Math.min(25, Math.max(1, Number(e.target.value) || 1)) })
                }
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs font-semibold text-cinza">Sábado conta?</span>
              <select
                className={CAMPO}
                value={regra.diaUtil}
                onChange={(e) => onRegra({ ...regra, diaUtil: e.target.value as TipoDeDiaUtil })}
              >
                <option value="bancario">Não — dia útil bancário</option>
                <option value="clt">Sim — art. 459 da CLT (folha de salário)</option>
              </select>
            </label>
          </>
        )}

        {periodicidade.tipo === "anual" && (
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs font-semibold text-cinza">Em que mês</span>
            <select
              className={CAMPO}
              value={periodicidade.mes}
              onChange={(e) => onPeriodicidade({ tipo: "anual", mes: Number(e.target.value) })}
            >
              {NOMES_DOS_MESES.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {regra.tipo === "n-dia-util" && regra.diaUtil === "clt" && (
        <p className="rounded-lg bg-alerta-fundo p-3 text-xs leading-relaxed text-alerta-tinta">
          Contar sábado é a exceção, e vale para pagamento de salário. As duas contagens
          divergem em 17 dos 24 meses de 2026-2027 — em setembro de 2026, por três dias.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Um decreto novo
// ---------------------------------------------------------------------------
function NovoDecreto({
  obrigacaoId,
  onMudou,
  onErro,
}: {
  obrigacaoId: string;
  onMudou: () => Promise<void>;
  onErro: (m: string | null) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [vence, setVence] = useState(true);
  const [dia, setDia] = useState(1);
  const [motivo, setMotivo] = useState("");

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="pt-4 text-xs font-medium text-link underline"
      >
        acrescentar decreto para um mês
      </button>
    );
  }

  return (
    <div className="pt-4">
      <h3 className="text-xs font-semibold text-cinza">Decreto de um mês só</h3>
      <p className="pt-1 text-xs leading-relaxed text-cinza">
        Vale apenas no mês escolhido e vence a regra ali. Para representar um prazo adiado
        para outro mês, são dois decretos: um dizendo que não vence no mês de origem, outro
        com a data nova.
      </p>
      <div className="grid gap-2 pt-2 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-cinza">Ano</span>
          <input
            className={CAMPO}
            type="number"
            min={2000}
            max={2100}
            value={ano}
            onChange={(e) => setAno(Number(e.target.value) || ano)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-cinza">Mês</span>
          <select className={CAMPO} value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {NOMES_DOS_MESES.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="flex items-center gap-2 pt-2 text-xs">
        <input
          type="checkbox"
          checked={vence}
          onChange={(e) => setVence(e.target.checked)}
          className="size-4 accent-laranja"
        />
        <span className="text-tinta">Vence neste mês (desmarque para suspender)</span>
      </label>
      {vence && (
        <label className="flex flex-col gap-1 pt-2 text-xs">
          <span className="text-cinza">Dia do vencimento</span>
          <input
            className={CAMPO}
            type="number"
            min={1}
            max={31}
            value={dia}
            onChange={(e) => setDia(Math.min(31, Math.max(1, Number(e.target.value) || 1)))}
          />
        </label>
      )}
      <label className="flex flex-col gap-1 pt-2 text-xs">
        <span className="text-cinza">Motivo (aparece para o aluno)</span>
        <input
          className={CAMPO}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Portaria RFB 000/2026 — prazo prorrogado"
        />
      </label>
      <div className="flex gap-2 pt-3">
        <button
          type="button"
          onClick={() =>
            void (async () => {
              if (!motivo.trim()) {
                onErro("O decreto precisa de um motivo — é o que o aluno lê no lugar da regra.");
                return;
              }
              const r = await salvarExcecao({
                obrigacaoId,
                ano,
                mes,
                data: vence
                  ? `${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`
                  : null,
                motivo: motivo.trim(),
              });
              if (!r.ok) onErro(r.mensagem);
              else {
                setAberto(false);
                setMotivo("");
                await onMudou();
              }
            })()
          }
          className="rounded-full bg-laranja px-4 py-2 text-xs font-semibold text-azul"
        >
          Gravar decreto
        </button>
        <button
          type="button"
          onClick={() => setAberto(false)}
          className="rounded-full px-4 py-2 text-xs font-medium text-cinza"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feriados de exceção
// ---------------------------------------------------------------------------
function Feriados({
  catalogo,
  onMudou,
  onErro,
}: {
  catalogo: Catalogo;
  onMudou: () => Promise<void>;
  onErro: (m: string | null) => void;
}) {
  const [data, setData] = useState("");
  const [nome, setNome] = useState("");
  const [acao, setAcao] = useState<"acrescentar" | "remover">("acrescentar");
  const [abrangencia, setAbrangencia] = useState<
    "nacional" | "estadual" | "municipal" | "facultativo"
  >("estadual");
  const [uf, setUf] = useState("");
  const [municipio, setMunicipio] = useState("");

  const lista = [
    ...catalogo.feriadosExtras.map((f) => ({ ...f, acao: "acrescentar" as const })),
    ...catalogo.feriadosRemovidos.map((d) => ({
      data: d,
      nome: "(removido)",
      nacional: false,
      acao: "remover" as const,
    })),
  ].sort((a, b) => (a.data < b.data ? -1 : 1));

  return (
    <div className="pt-5">
      <p className="max-w-2xl pb-4 text-sm leading-relaxed text-cinza">
        Os <strong className="text-tinta">feriados nacionais são calculados</strong> — os
        dez da lei, incluindo a Paixão de Cristo, que é móvel. Esta tabela guarda só
        exceção: feriado estadual ou municipal que a casa queira considerar, e a remoção de
        um nacional quando um decreto o transfere. Carnaval e Corpus Christi não estão na
        lista calculada porque são ponto facultativo, não feriado nacional.
      </p>

      <section
        role="group"
        aria-labelledby="novo-feriado"
        className="rounded-2xl border-[1.5px] border-borda bg-superficie p-4"
      >
        <h2 id="novo-feriado" className="pb-3 text-sm font-semibold text-titulo">
          Acrescentar exceção
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs font-semibold text-cinza">Data</span>
            <input
              className={CAMPO}
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs font-semibold text-cinza">Nome</span>
            <input
              className={CAMPO}
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Revolução Constitucionalista"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs font-semibold text-cinza">Ação</span>
            <select
              className={CAMPO}
              value={acao}
              onChange={(e) => setAcao(e.target.value as "acrescentar" | "remover")}
            >
              <option value="acrescentar">Passa a ser feriado</option>
              <option value="remover">Deixa de ser feriado</option>
            </select>
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs font-semibold text-cinza">Abrangência</span>
            <select
              className={CAMPO}
              value={abrangencia}
              onChange={(e) =>
                setAbrangencia(
                  e.target.value as "nacional" | "estadual" | "municipal" | "facultativo",
                )
              }
            >
              <option value="estadual">Estadual</option>
              <option value="municipal">Municipal</option>
              <option value="nacional">Nacional</option>
              <option value="facultativo">Ponto facultativo</option>
            </select>
          </label>
          {(abrangencia === "estadual" || abrangencia === "municipal") && (
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs font-semibold text-cinza">UF</span>
              <input
                className={CAMPO}
                value={uf}
                onChange={(e) => setUf(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="SP"
                maxLength={2}
              />
            </label>
          )}
          {abrangencia === "municipal" && (
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs font-semibold text-cinza">Município</span>
              <input
                className={CAMPO}
                value={municipio}
                onChange={(e) => setMunicipio(e.target.value)}
                placeholder="São Paulo"
              />
            </label>
          )}
        </div>
        <button
          type="button"
          onClick={() =>
            void (async () => {
              if (!data || !nome.trim()) {
                onErro("O feriado precisa de data e nome.");
                return;
              }
              const r = await salvarFeriado({
                data,
                nome: nome.trim(),
                acao,
                abrangencia,
                uf,
                municipio,
              });
              if (!r.ok) onErro(r.mensagem);
              else {
                setData("");
                setNome("");
                await onMudou();
              }
            })()
          }
          className="mt-4 rounded-full bg-laranja px-5 py-2.5 text-sm font-semibold text-azul"
        >
          Gravar
        </button>
      </section>

      {lista.length > 0 && (
        <ul className="flex flex-col gap-2 pt-4">
          {lista.map((f) => (
            <li
              key={`${f.data}-${f.nome}`}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border-[1.5px] border-borda bg-superficie p-3 text-sm"
            >
              <span className="text-tinta">
                <strong>{dataBr(f.data)}</strong> — {f.nome}{" "}
                <Etiqueta tom={f.acao === "remover" ? "erro" : "neutro"}>
                  {f.acao === "remover" ? "deixa de ser feriado" : "é feriado"}
                </Etiqueta>
              </span>
              <button
                type="button"
                onClick={() =>
                  void (async () => {
                    const r = await excluirFeriado(f.data, f.nome);
                    if (!r.ok) onErro(r.mensagem);
                    else await onMudou();
                  })()
                }
                className="text-xs font-medium text-cinza underline transition hover:text-erro-tinta"
              >
                apagar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
